/**
 * Auth Bootstrap Module
 * Handles MSAL initialization with support for Nested App Authentication (NAA) in Teams
 */

import {
  PublicClientApplication,
  type Configuration,
  type IPublicClientApplication,
  LogLevel,
  createNestablePublicClientApplication,
} from '@azure/msal-browser'
import { detectTeamsContext, type TeamsContextResult } from './teamsContext'

/** Runtime configuration loaded from config.json */
interface RuntimeConfig {
  VITE_AZURE_CLIENT_ID: string
  VITE_AZURE_TENANT_ID: string
  VITE_API_BASE_URL: string
  VITE_AZURE_API_APP_URI: string
}

/** Cached runtime config */
let runtimeConfig: RuntimeConfig | null = null

/** Cached MSAL instance */
let msalInstance: IPublicClientApplication | null = null

/** Cached Teams context result */
let teamsContextResult: TeamsContextResult | null = null

/** Bootstrap promise to ensure single initialization */
let bootstrapPromise: Promise<IPublicClientApplication> | null = null

// Declare the window runtime config type
declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig
  }
}

/**
 * Loads runtime config from various sources as a fallback when Vite env vars are unavailable.
 * Priority: 1) import.meta.env  2) window.__RUNTIME_CONFIG__ (from runtime-config.js)
 */
async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (runtimeConfig) {
    return runtimeConfig
  }

  // First try import.meta.env
  if (import.meta.env.VITE_AZURE_CLIENT_ID &&
      import.meta.env.VITE_AZURE_TENANT_ID &&
      import.meta.env.VITE_AZURE_API_APP_URI) {
    runtimeConfig = {
      VITE_AZURE_CLIENT_ID: import.meta.env.VITE_AZURE_CLIENT_ID,
      VITE_AZURE_TENANT_ID: import.meta.env.VITE_AZURE_TENANT_ID,
      VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL || '',
      VITE_AZURE_API_APP_URI: import.meta.env.VITE_AZURE_API_APP_URI,
    }
    console.log('[Auth] Using Vite environment variables')
    return runtimeConfig
  }

  // Fall back to window.__RUNTIME_CONFIG__ (set by runtime-config.js in index.html)
  if (window.__RUNTIME_CONFIG__?.VITE_AZURE_CLIENT_ID &&
      window.__RUNTIME_CONFIG__?.VITE_AZURE_TENANT_ID &&
      window.__RUNTIME_CONFIG__?.VITE_AZURE_API_APP_URI) {
    runtimeConfig = window.__RUNTIME_CONFIG__
    console.log('[Auth] Using window.__RUNTIME_CONFIG__ from runtime-config.js')
    return runtimeConfig
  }

  throw new Error(
    'Missing environment variables. ' +
    'Either set VITE_AZURE_CLIENT_ID, VITE_AZURE_TENANT_ID, VITE_AZURE_API_APP_URI in .env.local, ' +
    'or ensure public/runtime-config.js sets window.__RUNTIME_CONFIG__.'
  )
}

/** Get client ID from config */
export function getClientId(): string {
  if (!runtimeConfig?.VITE_AZURE_CLIENT_ID) {
    throw new Error('Config not loaded. Call bootstrapAuth() first.')
  }
  return runtimeConfig.VITE_AZURE_CLIENT_ID
}

/** Get tenant ID from config */
export function getTenantId(): string {
  if (!runtimeConfig?.VITE_AZURE_TENANT_ID) {
    throw new Error('Config not loaded. Call bootstrapAuth() first.')
  }
  return runtimeConfig.VITE_AZURE_TENANT_ID
}

/**
 * Get API Application ID URI (e.g. api://autopilot.sfi.microsoft.com/311f28cb-...)
 * This is the full Application ID URI from the API app registration in Entra ID.
 */
export function getApiAppUri(): string {
  if (!runtimeConfig?.VITE_AZURE_API_APP_URI) {
    throw new Error('Config not loaded. Call bootstrapAuth() first.')
  }
  const apiAppUri = runtimeConfig.VITE_AZURE_API_APP_URI
  if (!apiAppUri.startsWith('api://')) {
    throw new Error(`VITE_AZURE_API_APP_URI must start with "api://". Got: ${apiAppUri}`)
  }
  return apiAppUri.replace(/\/+$/, '')
}

/** Validate that config has been loaded */
function validateConfig(): void {
  if (!runtimeConfig) {
    throw new Error('Config not loaded. This should not happen.')
  }

  const missing: string[] = []
  if (!runtimeConfig.VITE_AZURE_CLIENT_ID) {
    missing.push('VITE_AZURE_CLIENT_ID')
  }
  if (!runtimeConfig.VITE_AZURE_TENANT_ID) {
    missing.push('VITE_AZURE_TENANT_ID')
  }
  if (!runtimeConfig.VITE_AZURE_API_APP_URI) {
    missing.push('VITE_AZURE_API_APP_URI')
  }

  if (missing.length > 0) {
    throw new Error(`Missing configuration values: ${missing.join(', ')}`)
  }
}

/**
 * Creates MSAL configuration with NAA support
 * @param isTeams - Whether running in Teams context
 */
function createMsalConfig(isTeams: boolean): Configuration {
  const clientId = getClientId()
  const tenantId = getTenantId()

  return {
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      // Disable automatic navigation for embedded scenarios
      navigateToLoginRequestUrl: false,
      // Enable NAA support
      supportsNestedAppAuth: true,
    },
    cache: {
      // Use sessionStorage for embedded scenarios (Teams security requirement)
      cacheLocation: isTeams ? 'sessionStorage' : 'localStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        logLevel: import.meta.env.DEV ? LogLevel.Verbose : LogLevel.Error,
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return
          }
          switch (level) {
            case LogLevel.Error:
              console.error(message)
              break
            case LogLevel.Warning:
              console.warn(message)
              break
            case LogLevel.Info:
              console.info(message)
              break
            case LogLevel.Verbose:
              console.debug(message)
              break
            default:
              console.log(message)
          }
        },
        piiLoggingEnabled: false,
      },
    },
  }
}

/**
 * Bootstraps authentication with NAA support
 * Creates the appropriate MSAL instance based on hosting context
 *
 * IMPORTANT: This must be called before rendering the app and the result
 * must be awaited to ensure proper initialization order
 *
 * @returns Promise resolving to initialized MSAL instance
 */
export async function bootstrapAuth(): Promise<IPublicClientApplication> {
  // Return existing promise if already bootstrapping
  if (bootstrapPromise) {
    return bootstrapPromise
  }

  bootstrapPromise = (async () => {
    // Return cached instance if already initialized
    if (msalInstance) {
      return msalInstance
    }

    // Load runtime config (tries import.meta.env first, then config.json)
    await loadRuntimeConfig()
    validateConfig()

    // Detect Teams context FIRST (required for NAA)
    teamsContextResult = await detectTeamsContext()
    const isTeams = teamsContextResult.isTeams

    // Create MSAL config
    const msalConfig = createMsalConfig(isTeams)

    // Create appropriate MSAL instance
    if (isTeams) {
      console.log('[Auth] Creating nestable PCA for Teams context')
      msalInstance = await createNestablePublicClientApplication(msalConfig)
    } else {
      console.log('[Auth] Creating standard PCA for browser context')
      msalInstance = await PublicClientApplication.createPublicClientApplication(msalConfig)
    }

    // Handle redirect promise
    try {
      const response = await msalInstance.handleRedirectPromise()
      if (response?.account) {
        msalInstance.setActiveAccount(response.account)
        console.log('[Auth] Set active account from redirect response')
      }
    } catch (error) {
      console.error('[Auth] Error handling redirect promise:', error)
    }

    // Set active account if not already set
    if (!msalInstance.getActiveAccount()) {
      const accounts = msalInstance.getAllAccounts()

      if (accounts.length > 0) {
        // Prefer account matching Teams login hint if available
        if (teamsContextResult.loginHint) {
          const matchingAccount = accounts.find(
            (a) =>
              a.username?.toLowerCase() === teamsContextResult!.loginHint?.toLowerCase()
          )
          if (matchingAccount) {
            msalInstance.setActiveAccount(matchingAccount)
            console.log('[Auth] Set active account from Teams login hint')
          } else {
            msalInstance.setActiveAccount(accounts[0])
            console.log('[Auth] Set first account as active (no Teams hint match)')
          }
        } else {
          msalInstance.setActiveAccount(accounts[0])
          console.log('[Auth] Set first account as active')
        }
      }
    }

    if (import.meta.env.DEV) {
      const apiAppUri = getApiAppUri()
      console.log('[Auth] Config:', {
        clientId: getClientId(),
        tenantId: getTenantId(),
        authority: `https://login.microsoftonline.com/${getTenantId()}`,
        apiAppUri,
        apiScope: `${apiAppUri}/Api.Access`,
        redirectUri: window.location.origin,
      })
    }

    return msalInstance
  })()

  return bootstrapPromise
}

/**
 * Gets the cached MSAL instance
 * @throws Error if bootstrapAuth() hasn't been called
 */
export function getMsalInstance(): IPublicClientApplication {
  if (!msalInstance) {
    throw new Error('MSAL not initialized. Call bootstrapAuth() first.')
  }
  return msalInstance
}

/**
 * Gets the cached Teams context result
 * @throws Error if bootstrapAuth() hasn't been called
 */
export function getTeamsContext(): TeamsContextResult {
  if (!teamsContextResult) {
    throw new Error('Auth not initialized. Call bootstrapAuth() first.')
  }
  return teamsContextResult
}

/**
 * Checks if running in Teams context
 * @throws Error if bootstrapAuth() hasn't been called
 */
export function isTeamsContext(): boolean {
  return getTeamsContext().isTeams
}
